import React, { useState } from 'react';
import { cachedInvoke } from '../../lib/apiCache';
import { ENTITY_CATEGORIES, normalizeEntities } from '../../lib/entityCategories';
import SoundstatsArtistDashboard from '../Soundstats/SoundstatsArtistDashboard';
import soundstatsService from '../../services/soundstatsService';
import FestivalDrawer from './FestivalDrawer';
import RecordDetailDrawer from './RecordDetailDrawer';
import ShowDrawer from './ShowDrawer';
import './entityActions.css';

// Per-reply entity action buttons + detail drawers. Shows the extracted names
// (no API at open). Artists open the Soundcharts artist-intelligence overview
// (using the UUID from the intermediate steps, or a name search when absent);
// events/festivals and venues open the record extracted directly from the MCP result.

export default function EntityActions({ entities, eventRecords, venueRecords }) {
  const [cat, setCat] = useState(null);
  const [soundstats, setSoundstats] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [recordDetail, setRecordDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState('');

  const norm = normalizeEntities(entities);
  const cats = ENTITY_CATEGORIES.filter((c) => (norm[c.key] || []).length > 0);
  if (cats.length === 0) return null;

  const openCategory = (key) => {
    setCat(key);
    setNotFound('');
  };
  const closeAll = () => {
    setCat(null);
    setSoundstats(null);
    setEventDetail(null);
    setRecordDetail(null);
    setShowDetail(null);
    setSearching(false);
    setNotFound('');
  };
  const closeDetail = () => {
    setSoundstats(null);
    setEventDetail(null);
    setRecordDetail(null);
    setShowDetail(null);
    setNotFound('');
  };

  const findRecord = (list, name) => {
    const arr = Array.isArray(list) ? list : [];
    const lc = (name || '').toLowerCase();
    return (
      arr.find((r) => (r.name || '').toLowerCase() === lc) ||
      arr.find((r) => (r.name || '').toLowerCase().includes(lc)) ||
      null
    );
  };

  const selectName = async (key, item) => {
    const name = item.name;
    setNotFound('');
    if (key === 'artists') {
      const uuid = (item && item.uuid) || '';
      if (uuid) {
        setSoundstats({ uuid, name });
        return;
      }
      setSearching(true);
      try {
        const r = await soundstatsService.searchArtist(name);
        const list = (r && r.artists) || [];
        const best = list[0] || null;
        if (best && best.uuid) setSoundstats({ uuid: best.uuid, name: best.name || name });
        else setNotFound(name);
      } catch {
        setNotFound(name);
      } finally {
        setSearching(false);
      }
      return;
    }
    if (key === 'events') {
      setSearching(true);
      try {
        const isFestival = item.type === 'festival';
        const r = await cachedInvoke(isFestival ? 'lyveDmsFestivals' : 'lyveDmsEvents', { q: name, pages: 1, perPage: 20 });
        const list = isFestival
          ? (r && r.data && r.data.festivals) || []
          : (r && r.data && r.data.events) || [];
        const lc = name.toLowerCase();
        const best =
          list.find((x) => (x.n || '').toLowerCase() === lc) ||
          list.find((x) => (x.n || '').toLowerCase().includes(lc)) ||
          null;
        if (best) setEventDetail({ item: best });
        else {
          const rec = findRecord(eventRecords, name);
          if (rec) setRecordDetail({ title: rec.name, record: rec.record, type: rec.type });
          else setNotFound(name);
        }
      } catch {
        const rec = findRecord(eventRecords, name);
        if (rec) setRecordDetail({ title: rec.name, record: rec.record, type: rec.type });
        else setNotFound(name);
      } finally {
        setSearching(false);
      }
      return;
    }
    if (key === 'venues') {
      const rec = findRecord(venueRecords, name);
      if (rec) setRecordDetail({ title: rec.name, record: rec.record, type: 'venue' });
      else setNotFound(name);
    }
    if (key === 'shows') {
      const slug = (item && (item.uuid || item.id || item.identifier)) || '';
      if (slug) setShowDetail({ slug, name });
      else setNotFound(name);
    }
  };

  const openCat = cat ? ENTITY_CATEGORIES.find((c) => c.key === cat) : null;
  const listItems = openCat ? norm[openCat.key] || [] : [];
  const showDrawer = openCat && !soundstats && !eventDetail && !recordDetail && !showDetail;

  return (
    <div className="ea-root">
      <div className="ea-buttons">
        {cats.map((c) => (
          <button key={c.key} className="ea-btn" onClick={() => openCategory(c.key)}>
            <span className="ea-ic">{c.icon}</span>
            <span>{c.label}</span>
            <span className="ea-ct">{(norm[c.key] || []).length}</span>
          </button>
        ))}
      </div>

      {showDrawer && (
        <div className="ea-root fdrawer">
          <div className="bg" onClick={closeAll} />
          <div className="panel">
            <div className="fhead">
              <div>
                <h3 className="ftitle">{openCat.label}</h3>
                <div className="fsub">{`${listItems.length} mentioned`}</div>
              </div>
              <div className="fhead-actions">
                <button className="fclose" onClick={closeAll}>
                  Close ✕
                </button>
              </div>
            </div>

            <div className="ea-list">
              {searching && (
                <div className="ea-loading">
                  <i /> Searching…
                </div>
              )}
              {notFound && !searching && <div className="ea-notfound">No match found for {notFound}.</div>}
              {listItems.map((item, i) => {
                const name = typeof item === 'string' ? item : (item && item.name) || '';
                const type = (item && item.type) || 'event';
                const uuid = (item && (item.uuid || item.id || item.identifier)) || '';
                return (
                  <button key={i} className="ea-item" onClick={() => selectName(openCat.key, { name, type, uuid })} disabled={searching}>
                    <span className="ea-item-name">{name || 'Unknown'}</span>
                    {uuid && <span className="ea-uuid">{uuid}</span>}
                    {openCat.key === 'events' && (
                      <span className={`ea-type ${type === 'festival' ? 'fest' : 'ev'}`}>
                        {type === 'festival' ? 'Festival' : 'Event'}
                      </span>
                    )}
                    <span className="ea-chev">›</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {soundstats && (
        <div className="ea-root fdrawer fdrawer-wide">
          <div className="bg" onClick={closeAll} />
          <div className="panel panel--wide">
            <div className="lyve-shell">
              <SoundstatsArtistDashboard
                uuid={soundstats.uuid}
                onBack={() => setSoundstats(null)}
                backLabel="← Back to artists"
                overviewOnly
              />
            </div>
          </div>
        </div>
      )}
      {eventDetail && <FestivalDrawer item={eventDetail.item} onClose={closeDetail} />}
      {recordDetail && (
        <RecordDetailDrawer
          title={recordDetail.title}
          record={recordDetail.record}
          type={recordDetail.type}
          onClose={closeDetail}
        />
      )}
      {showDetail && <ShowDrawer item={showDetail} onClose={closeDetail} />}
    </div>
  );
}
